import React, { useCallback, useEffect, useState } from 'react';
import { GetServerSideProps, NextPageWithLayout } from 'next';
import { useRouter } from 'next/router';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import extractFromContext from '@helpers/utils/extract-from-context';
import { getAppInitialProps } from '@helpers/initial-props';
import PageAuthenticationGuard from '@components/atoms/authentication/page-authentication-guard/page-authentication-guard';
import { useAnalytics } from '@context/analytics/analytics.context';
import { accountSettingsChangeTabEventPayload } from '@tracking/account-settings/account-settings-tracking';
import { useLegacyHashRedirect } from '@hooks/account-settings/use-legacy-hash-redirect';
import { ToastMessageProvider } from '@context/toast-message.context';
import DefaultLayout from '@layouts/default-layout';
import AccountSettingsLayout, {
  ACCOUNT_SETTINGS_TABS,
  AccountSettingsTab,
  DEFAULT_TAB,
} from '@layouts/account-settings-layout/account-settings-layout';
import AccountInfo from '@components/account-settings/account-info/account-info';
import AccountNotifications from '@components/account-settings/account-notifications/account-notifications';
import AccountAddresses from '@components/account-settings/account-addresses/account-addresses';
import AccountPayments from '@components/account-settings/account-payments/account-payments';
import { AccountSettingsChangeTabEvent } from '@interfaces/models/internal/analytics-event/account-settings-event';

const accountTabToTrackingLabelMap: Record<AccountSettingsTab, AccountSettingsChangeTabEvent['label']> = {
  [AccountSettingsTab.INFO]: 'account',
  [AccountSettingsTab.NOTIFICATIONS]: 'email-preferences',
  [AccountSettingsTab.ADDRESSES]: 'addresses',
  [AccountSettingsTab.PAYMENTS]: 'payment-methods',
};

const getTabFromQuery = (tab: string[] | string | undefined): AccountSettingsTab => {
  const tabParam = Array.isArray(tab) ? tab[0] : tab;
  return tabParam && ACCOUNT_SETTINGS_TABS.includes(tabParam as AccountSettingsTab)
    ? (tabParam as AccountSettingsTab)
    : DEFAULT_TAB;
};

const AccountSettingsPage: NextPageWithLayout = () => {
  const router = useRouter();
  const [activeTab, setActiveTab] = useState<AccountSettingsTab>(() => getTabFromQuery(router.query.tab));
  const { sendEvent } = useAnalytics();

  const handleTabChange = useCallback(
    (tab: AccountSettingsTab) => {
      if (tab === activeTab) {
        return;
      }
      sendEvent({
        type: 'event',
        payload: { ...accountSettingsChangeTabEventPayload, label: accountTabToTrackingLabelMap[tab] },
      });
      setActiveTab(tab);
      window.history.pushState(null, '', `/my-account/settings/${tab}`);
    },
    [activeTab, sendEvent],
  );

  useLegacyHashRedirect();

  useEffect(() => {
    const onPopState = () => {
      const pathTab = window.location.pathname.split('/').pop();
      setActiveTab(getTabFromQuery(pathTab));
    };

    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  return (
    <PageAuthenticationGuard redirectionUrl={'/'}>
      <ToastMessageProvider>
        <AccountSettingsLayout
          activeTab={activeTab}
          onTabChange={handleTabChange}
        >
          {activeTab === AccountSettingsTab.INFO && <AccountInfo />}
          {activeTab === AccountSettingsTab.NOTIFICATIONS && <AccountNotifications />}
          {activeTab === AccountSettingsTab.ADDRESSES && <AccountAddresses />}
          {activeTab === AccountSettingsTab.PAYMENTS && <AccountPayments />}
        </AccountSettingsLayout>
      </ToastMessageProvider>
    </PageAuthenticationGuard>
  );
};

AccountSettingsPage.Layout = [DefaultLayout];
AccountSettingsPage.PageType = 'AccountSettingsPage';

export const getServerSideProps: GetServerSideProps = async (context) => {
  const tab = (context.params?.tab as string[] | undefined)?.[0];

  if (tab && !ACCOUNT_SETTINGS_TABS.includes(tab as AccountSettingsTab)) {
    return { notFound: true };
  }

  const { locale } = extractFromContext(context);
  const initialProps = await getAppInitialProps(context);
  const translations = await serverSideTranslations(locale, [
    'default-layout',
    'vc-authentication',
    'account-settings',
    'common',
    'a11y',
    'reset-password',
  ]);

  return {
    props: {
      ...initialProps,
      ...translations,
    },
  };
};

export default AccountSettingsPage;
